import * as PrestigeSystem from '../game/PrestigeSystem.js';

/**
 * PrestigeStateManager handles all prestige/ascension state operations
 *
 * Provides a focused API for prestige management while keeping
 * actual state in GameState.state.prestige for save compatibility.
 */
export class PrestigeStateManager {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get the prestige state object (shorthand)
     */
    get prestige() {
        return this.gameState.state.prestige;
    }

    // ===== Read Operations =====

    /**
     * Get current prestige level
     * @returns {number} Current prestige level (0 = never prestiged)
     */
    getPrestigeLevel() {
        return this.prestige?.level || 0;
    }

    /**
     * Get mile earning bonus from prestige
     * @returns {number} Mile earning bonus as decimal (0.15 = 15% bonus)
     */
    getMileEarningBonus() {
        return this.prestige?.mileEarningBonus || 0;
    }

    /**
     * Get skill bonus for a specific skill type
     * @param {string} skillType - Skill type (e.g., 'baseSpeed', 'acceleration')
     * @returns {number} Skill bonus as decimal (0.15 = 15% bonus)
     */
    getSkillBonus(skillType) {
        return this.prestige?.skillBonuses?.[skillType] || 0;
    }

    /**
     * Get all skill bonuses as a copy (not reference)
     * @returns {object} Copy of skill bonuses object
     */
    getAllSkillBonuses() {
        const defaults = {
            baseSpeed: 0,
            acceleration: 0,
            maxSpeed: 0,
            cruiseDuration: 0,
            cruiseCooldown: 0,
            momentum: 0,
            momentumRate: 0
        };
        return { ...defaults, ...(this.prestige?.skillBonuses || {}) };
    }

    // ===== Prestige System Delegation =====

    /**
     * Get ascension bonus for a specific stat type
     * Delegates to PrestigeSystem for calculation
     * @param {string} statType - Stat type to get ascension bonus for
     * @returns {number} Ascension bonus value
     */
    getAscensionBonus(statType) {
        // The ascension bonus is the sum of prestige bonuses for that stat
        return this.getSkillBonus(statType);
    }

    /**
     * Get all ascension bonuses aggregated
     * @returns {object} Object with all ascension bonus values
     */
    getAllAscensionBonuses() {
        return this.getAllSkillBonuses();
    }

    /**
     * Check if player can prestige (is on final car)
     * Delegates to gameState for car progression check
     * @returns {boolean} True if prestige is available
     */
    canPrestige() {
        return this.gameState.isPrestigeAvailable();
    }

    /**
     * Get prestige preview info including bonuses that would be gained
     * @returns {object} Prestige info with level, bonuses, and preview
     */
    getPrestigeInfo() {
        const level = this.getPrestigeLevel();
        const currentMiles = this.gameState.state.miles;
        const totalMiles = this.gameState.state.totalMiles;

        // Calculate what bonuses would be gained if prestiging now
        const preview = PrestigeSystem.calculatePrestigeBonus(totalMiles, currentMiles, level);

        return {
            level,
            mileEarningBonus: this.getMileEarningBonus(),
            skillBonuses: this.getAllSkillBonuses(),
            tierName: PrestigeSystem.getPrestigeTierName(level),
            tierColor: PrestigeSystem.getPrestigeTierColor(level),
            canPrestige: this.canPrestige(),
            preview: {
                bonusGained: preview.total,
                levelMultiplier: preview.levelMultiplier,
                baseBonus: preview.base,
                extraBonus: preview.extra
            }
        };
    }

    // ===== Write Operations =====

    /**
     * Perform prestige - reset progress and gain permanent bonuses
     * Delegates to GameState.performPrestige for the actual reset logic
     * @param {string} selectedSkill - The skill to apply the bonus to
     * @returns {object|false} Result object with new prestige info, or false if failed
     */
    performPrestige(selectedSkill) {
        // Delegate to GameState which handles the full reset logic
        return this.gameState.performPrestige(selectedSkill);
    }

    // ===== Utility Methods =====

    /**
     * Get level multiplier for current prestige level
     * @returns {number} Multiplier (1.0, 1.25, 1.5, etc.)
     */
    getLevelMultiplier() {
        return PrestigeSystem.getLevelMultiplier(this.getPrestigeLevel());
    }

    /**
     * Format a bonus for display as percentage
     * @param {number} bonus - Bonus as decimal
     * @returns {string} Formatted string like "+15.5%"
     */
    formatBonus(bonus) {
        return PrestigeSystem.formatBonusPercent(bonus);
    }

    /**
     * Format a bonus for display as multiplier
     * @param {number} bonus - Bonus as decimal
     * @returns {string} Formatted string like "x1.15"
     */
    formatBonusAsMultiplier(bonus) {
        return PrestigeSystem.formatBonusMultiplier(bonus);
    }

    /**
     * Get selectable skills for prestige
     * @returns {object} SELECTABLE_SKILLS from PrestigeSystem
     */
    getSelectableSkills() {
        return PrestigeSystem.SELECTABLE_SKILLS;
    }
}
